import "../reports.css";
import { ReactNode, useEffect, useState } from "react";
import { Pagination } from "antd";
import { useApiFetch, useDimensions, useLanguage } from "../../../hooks";
import {
  ReportOperationType,
  ReportSalesPageProps,
  ReportSalesProps,
  ReportTabsBaseProps,
  salesQueryParams,
} from "../reports.props";
import {
  BoardBody,
  BoardDotStatus,
  BoardHead,
  BoardTd,
  BoardTdMobileItem,
  BoardTh,
  BoardTr,
  PermissionGuard,
} from "../../../components";
import { AuthRolType } from "../../../context";
import { vars } from "../../../assets";
import {
  buildQueryString,
  formatAmount,
  formatDateTimeToLocal,
} from "../../../utils";

interface ReportSalesTableProps extends ReportTabsBaseProps {
  // Endpoint de la vista: /transactions (marca) u /ownsales (canal).
  endpoint: (query?: string) => string;
  allowedRoles: AuthRolType[];
}

interface SalesColumn {
  key: keyof ReportSalesProps;
  header: string;
  render: (sale: ReportSalesProps) => ReactNode;
  alignRight?: boolean; // columnas numéricas
}

type SortState = { key: keyof ReportSalesProps; direction: "asc" | "desc" };

const PAGE_SIZES = [25, 50, 100, 250];
const SEARCH_DEBOUNCE_MS = 400;

const OPERATION_COLORS: Record<
  ReportOperationType,
  { primary: string; secondary: string }
> = {
  activation: { primary: "#1FC16B", secondary: "#1FC16B1A" },
  refund: { primary: "#4A4A4A", secondary: "#DFB40033" },
  cancellation: { primary: "#D00416", secondary: "#FB37481A" },
};

// Activación anulada o devuelta después: sigue siendo una activación, pero se
// distingue para que se vea su relación con la anulación/devolución.
const REVERSED_ACTIVATION_COLORS = { primary: "#E07B00", secondary: "#E07B001A" };
const REVERSED_ACTIVATION_LABEL_KEYS = {
  cancellation: "cancelledActivation",
  refund: "refundedActivation",
} as const;

const text = (value: unknown): ReactNode => <p>{String(value ?? "")}</p>;

// Informe de ventas (marca o canal). Columnas acordadas con el cliente a partir
// de los informes manuales (docs/Report_*.xlsx): PRODUCTO, FECHA, RETAILER,
// FITERMINAL, PUNTO DE VENTA, OPERACIÓN, IMPORTE (valor con signo), EAN y CÓDIGO PROD.
//
// Paginado en el servidor (un canal puede tener cientos de miles de operaciones en
// un mes): filtros, búsqueda y orden se aplican en la consulta, no en el navegador.
const ReportSalesTable: React.FC<ReportSalesTableProps> = ({
  endpoint,
  allowedRoles,
  search = "",
  filters,
  dateRange,
  setExportDisabled,
  setErrorFetchData,
}) => {
  const { t } = useLanguage();
  const { width } = useDimensions();
  const thead = t.reportsScreen.table.thead;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [fetchData, , loading, error] = useApiFetch<ReportSalesPageProps>();
  const [result, setResult] = useState<ReportSalesPageProps>({
    items: [],
    total: 0,
    page: 1,
    pageSize: 50,
  });
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [sort, setSort] = useState<SortState | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  // La búsqueda se envía cuando se deja de escribir.
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const operationText = (sale: ReportSalesProps): string => {
    const tbody = t.reportsScreen.table.tbody;
    if (sale.operation === "activation" && sale.reversedBy) {
      return tbody[REVERSED_ACTIVATION_LABEL_KEYS[sale.reversedBy]];
    }
    return tbody[sale.operation] ?? sale.operation;
  };

  const renderOperation = (sale: ReportSalesProps) => {
    const colors = (sale.operation === "activation" && sale.reversedBy
      ? REVERSED_ACTIVATION_COLORS
      : OPERATION_COLORS[sale.operation]) ?? {
      primary: vars.colorBlack,
      secondary: vars.colorWhiteMedium,
    };
    return (
      <BoardDotStatus
        pColor={colors.primary}
        sColor={colors.secondary}
        text={operationText(sale)}
      />
    );
  };

  const columns: SalesColumn[] = [
    {
      key: "productName",
      header: thead.product,
      render: (s) => <p className="board-bold">{s.productName}</p>,
    },
    {
      key: "date",
      header: thead.date,
      render: (s) => text(formatDateTimeToLocal(s.date)),
    },
    { key: "retailer", header: thead.retailer, render: (s) => text(s.retailer) },
    {
      key: "fiTerminal",
      header: thead.fiTerminal,
      render: (s) => text(s.fiTerminal),
    },
    {
      key: "establishment",
      header: thead.establishment,
      render: (s) => text(s.establishment),
    },
    { key: "operation", header: thead.operation, render: renderOperation },
    {
      key: "amount",
      header: thead.amount,
      render: (s) => text(formatAmount(s.amount)),
      alignRight: true,
    },
    { key: "ean", header: thead.ean, render: (s) => text(s.ean) },
    { key: "idProd", header: thead.idprod, render: (s) => text(s.idProd) },
  ];

  // Ciclo de orden por columna: ascendente -> descendente -> sin orden (fecha desc).
  const toggleSort = (key: keyof ReportSalesProps) => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, direction: "asc" };
      return prev.direction === "asc" ? { key, direction: "desc" } : null;
    });
  };

  const params = salesQueryParams(filters, dateRange, debouncedSearch);
  const paramsKey = JSON.stringify(params);

  // Cualquier cambio de filtros, búsqueda u orden vuelve a la primera página.
  useEffect(() => setPage(1), [paramsKey, sort?.key, sort?.direction, pageSize]);

  useEffect(() => setIsMobile(width <= 1200), [width]);
  useEffect(() => setExportDisabled(isMobile), [isMobile]);

  useEffect(() => {
    // El flag evita que una respuesta obsoleta sobrescriba una más reciente.
    let cancelled = false;
    (async () => {
      const query = buildQueryString({
        ...params,
        page: String(page),
        pageSize: String(pageSize),
        sort: sort?.key,
        direction: sort?.direction,
      });
      const d = await fetchData(endpoint(query), { method: "GET" });
      if (cancelled || !d?.data) return;
      setResult(d.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [paramsKey, page, pageSize, sort?.key, sort?.direction]);

  useEffect(() => {
    error?.length && setErrorFetchData(true);
  }, [error]);

  const rows = result.items;
  const from = result.total === 0 ? 0 : (result.page - 1) * result.pageSize + 1;
  const to = Math.min(result.total, (result.page - 1) * result.pageSize + rows.length);
  const rangeText = t.reportsScreen.pagination.range
    .replace("{from}", from.toLocaleString("es-ES"))
    .replace("{to}", to.toLocaleString("es-ES"))
    .replace("{total}", result.total.toLocaleString("es-ES"));

  return (
    <PermissionGuard allowedRoles={allowedRoles}>
      <BoardHead>
        <BoardTr>
          {isMobile ? (
            <BoardTh
              onClick={() => toggleSort("productName")}
              text={thead.product}
            />
          ) : (
            columns.map((column) => (
              <BoardTh
                key={column.key}
                className={column.alignRight ? "board-align-right" : undefined}
                onClick={() => toggleSort(column.key)}
                text={column.header}
              />
            ))
          )}
        </BoardTr>
      </BoardHead>

      <BoardBody isMobile={isMobile} isLoading={loading}>
        {rows.map((sale, index) => {
          const id: string = `${sale.id}-${index}`;
          return (
            <BoardTr key={`reports-sale-${id}`}>
              {isMobile ? (
                <BoardTd
                  hasClassNameW100
                  hasClassNameDirection={false}
                  mobile={{
                    isMobile: true,
                    isActive: activeId === id,
                    onClick: () =>
                      setActiveId((prev) => (prev === id ? null : id)),
                    text: sale.productName ?? "",
                  }}
                >
                  {columns.map((column) => (
                    <BoardTdMobileItem key={column.key} title={column.header}>
                      {column.render(sale)}
                    </BoardTdMobileItem>
                  ))}
                </BoardTd>
              ) : (
                columns.map((column) => (
                  <BoardTd
                    key={column.key}
                    className={column.alignRight ? "board-align-right" : ""}
                    hasClassNameW100={column.key === "productName"}
                  >
                    {column.render(sale)}
                  </BoardTd>
                ))
              )}
            </BoardTr>
          );
        })}
      </BoardBody>

      {/* "Registros X al Y de Z" y paginación */}
      <tfoot className="report-pagination-foot">
        <tr>
          <td colSpan={isMobile ? 1 : columns.length}>
            <div className="report-pagination">
              <span className="report-pagination-range">{rangeText}</span>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={result.total}
                pageSizeOptions={PAGE_SIZES}
                showSizeChanger
                size="small"
                locale={{ items_per_page: t.reportsScreen.pagination.perPage }}
                onChange={(nextPage, nextSize) => {
                  if (nextSize !== pageSize) {
                    setPageSize(nextSize);
                  } else {
                    setPage(nextPage);
                  }
                }}
              />
            </div>
          </td>
        </tr>
      </tfoot>
    </PermissionGuard>
  );
};

export default ReportSalesTable;
